import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Input, Select } from '../Input/Input';
import _ from 'lodash';
import { ValidateAllLoadAdjustments, getAdjustmentsWarningMessage } from '../Util/ValidateLoadAdjustments';
import PropTypes from 'prop-types';

export const AdjustmentsModal = React.forwardRef(
  ({ adjustments, setAdjustments, currencyOptions, show, onHide, readOnly }, ref) => {
    const [formValues, setFormValues] = useState(_.cloneDeep(adjustments));
    const [disableSaveButton, setDisableSaveButton] = useState(false);

    const setSaveButtonState = () => {
      const allAdjustmentsAreValid = ValidateAllLoadAdjustments(formValues);
      setDisableSaveButton(!allAdjustmentsAreValid);
    };

    const updateAdjustment = (name, type, value) => {
      setFormValues(
        formValues.map((adjustment) => {
          if (adjustment.name === name) {
            if (type === 'amount') {
              adjustment.amount = value;
            } else if (type === 'currency') {
              adjustment.currency = value;
            }
          }
          return adjustment;
        })
      );
      setSaveButtonState();
    };

    const save = () => {
      setAdjustments(
        _.cloneDeep(
          formValues.map((adj) => {
            adj.error = undefined;
            return adj;
          })
        )
      );
      onHide();
    };

    const close = () => {
      setFormValues(_.cloneDeep(adjustments));
      onHide();
    };

    useEffect(() => {
      if (show) {
        setFormValues(_.cloneDeep(adjustments));
        setSaveButtonState();
      }
    }, [show]);

    return (
      <Modal centered animation={false} show={show} onHide={close} dialogClassName="qa-wide-modal">
        <Modal.Header>
          <Modal.Title>Adjustments</Modal.Title>
          <button type="button" className="btn-close" aria-label="Close" onClick={close} />
        </Modal.Header>
        <Modal.Body className="pb-2">
          {formValues?.length && (
            <form ref={ref}>
              <div className="d-flex flex-column justify-content-center">
                {formValues.map((adjustment, index) => {
                  const { warningMessage, isError } = getAdjustmentsWarningMessage(adjustment);

                  return (
                    <div key={{ index }} className="d-flex flex-column mb-2">
                      <p className="m-0">
                        <strong>{adjustment.display}</strong>
                      </p>
                      <section className="d-flex flex-row justify-content-between">
                        <div style={{ width: '48%' }}>
                          <Input
                            label="Amount"
                            name={adjustment.name}
                            type="text"
                            value={adjustment.amount}
                            error={adjustment.error}
                            onChange={(e) => updateAdjustment(adjustment.name, 'amount', e.target.value)}
                            required={adjustment.required || adjustment.currency !== ''}
                            data-min-valid-amount={adjustment.minValidAmount}
                            data-max-valid-amount={adjustment.maxValidAmount}
                            disabled={readOnly}
                          />
                        </div>
                        <div style={{ width: '48%' }}>
                          <Select
                            label="Currency"
                            name={`${adjustment.name} Currency`}
                            data={adjustment.currency}
                            setData={(val) => updateAdjustment(adjustment.name, 'currency', val)}
                            options={currencyOptions}
                            required={adjustment.required || adjustment.amount !== ''}
                            disabled={readOnly}
                          />
                        </div>
                      </section>
                      {warningMessage && (
                        <span className={`${isError ? 'input-error-box-filled' : 'input-warning-box'} my-1`}>
                          {warningMessage}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </form>
          )}
        </Modal.Body>
        {!readOnly && (
          <Modal.Footer className="flex-row justify-content-between m-0 p-3">
            <button type="button" className="stepper-button red m-0" onClick={close}>
              Cancel
            </button>
            <button
              className="stepper-button m-0"
              onClick={save}
              style={{ display: 'flex' }}
              disabled={disableSaveButton}
            >
              Save
            </button>
          </Modal.Footer>
        )}
      </Modal>
    );
  }
);

AdjustmentsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

export default AdjustmentsModal;
